import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ActivityDrawerCloseActionType,
  CloseAction,
  TimelineActivityDrawerService,
} from './timeline-activity-drawer.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { ActivitiesGateway } from '@wilson/api/gateway';
import { ActivityCategoriesState } from '@wilson/activity-categories/data';
import {
  Observable,
  catchError,
  finalize,
  firstValueFrom,
  map,
  of,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  Activity,
  ActivityCategory,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  OperationStatus,
  ResolvedActivity,
  ShiftWithActivitiesWithLocations,
} from '@wilson/interfaces';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  ActivityDrawerFormComponent,
  FormValue,
} from './ActivityDrawerForm/activity-drawer-form.component';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Store } from '@ngxs/store';
import { SetDemarcatorRange } from '@wilson/shift-timeline/state';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { ActivityDrawerActionsSectionComponent } from './ActivityDrawerActionsSection/activity-drawer-actions-section.component';
import { WilsonApp } from '@wilson/interfaces';
import { ShiftsService } from '@wilson/shifts';
import { TimelineCommonOperationsService } from '../../../services/timeline-common-operations.service';
import { LastUpdateStatusComponent } from '@wilson/non-domain-specific/drawer-common';
import { ShiftDeleteConfirmationService } from '@wilson/non-domain-specific/shift-common-actions';
import { NzAlertModule } from 'ng-zorro-antd/alert';

interface CloseDrawerInput {
  action?: CloseAction;
}

@Component({
  selector: 'wilson-timeline-activity-drawer',
  imports: [
    CommonModule,
    NzDrawerModule,
    TranslateModule,
    NzButtonModule,
    ActivityDrawerFormComponent,
    NzSkeletonModule,
    ActivityDrawerActionsSectionComponent,
    LastUpdateStatusComponent,
    NzAlertModule,
  ],
  templateUrl: './timeline-activity-drawer.component.html',
  styleUrls: ['./timeline-activity-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineActivityDrawerComponent {
  activityDrawerService = inject(TimelineActivityDrawerService);
  private shiftService = inject(ShiftsService);
  private activitiesService = inject(ActivitiesGateway);
  private nzMessage = inject(NzMessageService);
  private translate = inject(TranslateService);
  private store = inject(Store);
  private readonly modalService = inject(NzModalService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly shiftDeleteConfirmationService = inject(
    ShiftDeleteConfirmationService,
  );

  OperationStatus = OperationStatus;
  WilsonApp = WilsonApp;

  readonly isLoading = signal(false);
  readonly isError = signal(false);
  formValue!: FormValue;

  readonly selectedActivity!: Signal<undefined | null | ResolvedActivity>;
  readonly shiftActivities!: Signal<undefined | null | Activity[]>;
  selectedShift$!: Observable<ShiftWithActivitiesWithLocations | null>;

  readonly isLastActivity!: Signal<boolean | null | undefined>;
  readonly isSuccessfullySaved = signal<boolean>(false);
  updatedAt!: string | undefined;
  activityUserId!: string | null;

  activityCategories$!: Observable<ActivityCategory[]>;

  constructor() {
    this.selectedActivity = toSignal(
      toObservable(this.activityDrawerService.selectedActivityId).pipe(
        switchMap((selectedActivityId: string | null) => {
          if (selectedActivityId) {
            this.isLoading.set(true);
            return this.getResolvedActivityStream(selectedActivityId)
              .pipe(
                tap((activity) => {
                  if (activity) {
                    this.updatedAt = activity.updatedAt;
                  } else {
                    this.isError.set(true);
                    this.isLoading.set(false);
                  }
                }),
              )
              .pipe(
                catchError(() => {
                  this.isLoading.set(false);
                  this.isError.set(true);
                  return of(null);
                }),
              );
          } else {
            return of(null);
          }
        }),
      ),
    );

    this.activityCategories$ = toObservable(this.selectedActivity).pipe(
      switchMap((activity) =>
        this.store.select(
          ActivityCategoriesState.selectableCategoriesSortedByName(
            activity?.activityCategoryId ?? null,
          ),
        ),
      ),
    );

    this.selectedShift$ = toObservable(this.selectedActivity).pipe(
      switchMap((activity: ResolvedActivity | null | undefined) => {
        const shiftId = activity?.shiftId;
        if (shiftId) {
          return this.shiftService
            .getShifts<ShiftWithActivitiesWithLocations>({
              where: {
                id: shiftId,
              },
              relations: [
                'activities',
                'activities.startLocation',
                'activities.endLocation',
              ],
            })
            .pipe(
              map((payload) => payload.data[0]),
              tap((shift) => {
                if (!shift) {
                  console.error('Shift not found');
                } else {
                  this.activityUserId = shift.userId;
                }
              }),
              finalize(() => {
                this.isLoading.set(false);
              }),
              catchError(() => of(null)),
            );
        } else {
          return of(null);
        }
      }),
    );

    this.isLastActivity = toSignal(
      this.selectedShift$.pipe(
        startWith(null),
        map((shift) => {
          return shift && shift.activities && shift.activities?.length <= 1;
        }),
      ),
    );
  }

  private getResolvedActivityStream(
    selectedActivityId: string,
  ): Observable<ResolvedActivity | null> {
    return this.activitiesService
      .getResolvedActivity(selectedActivityId)
      .pipe(map((activities: ResolvedActivity[]) => activities[0]));
  }

  async updateActivity(): Promise<void> {
    this.isSuccessfullySaved.set(false);
    try {
      await this.activitiesService.updateActivity({
        id: this.formValue.id as string,
        name: this.formValue.name as string,
        startLocation: this.formValue.startLocation,
        startLocationId: this.formValue.startLocation?.id,
        endLocation: this.formValue.endLocation,
        endLocationId: this.formValue.endLocation?.id,
        viaLocationIds: this.formValue.viaPointIds || [],
        startDatetime: this.formValue.startDateTime as string,
        endDatetime: this.formValue.endDateTime as string,
        activityCategoryId: this.formValue.activityCategory?.id,
        agreementId:
          this.formValue.agreement?.[this.formValue.agreement?.length - 1] ||
          null,
        professionId: this.formValue.professionId || null,
        orderNumber: this.formValue.orderNumber || null,
        wagonNumber: this.formValue.wagonNumber || null,
        locomotiveNumber: this.formValue.locomotiveNumber || null,
      });

      const resolvedActivity = await firstValueFrom(
        this.getResolvedActivityStream(this.formValue.id as string),
      );
      this.updatedAt = resolvedActivity?.updatedAt;

      this.activityDrawerService.updateActivity({
        activity:
          resolvedActivity as ActivityWithCategoryAndLocationsAndReportedDetails,
        type: ActivityDrawerCloseActionType.UPDATE,
      });
      if (this.activityUserId) {
        this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
          this.activityUserId,
        ]);
      }
      this.isSuccessfullySaved.set(true);
    } catch {
      this.isSuccessfullySaved.set(false);
      this.nzMessage.error(this.translate.instant('general.error'));
    }
  }

  async closeDrawer(input?: CloseDrawerInput): Promise<void> {
    this.activityDrawerService.closeDrawer(input?.action);

    this.store.dispatch(
      new SetDemarcatorRange([
        {
          left: null,
          right: null,
        },
      ]),
    );
  }

  async handleDeletion(
    activity: ResolvedActivity | null | undefined,
    isLastActivity: boolean,
  ): Promise<void> {
    if (activity) {
      let shouldUpdateKpis = false;
      const shouldDeleteShift = !!(isLastActivity && activity.shiftId);

      if (shouldDeleteShift) {
        const shift = await firstValueFrom(this.selectedShift$.pipe(take(1)));

        if (shift) {
          const confirmed =
            await this.shiftDeleteConfirmationService.confirmShiftDelete({
              name: shift.name,
              state: shift.state,
              activities: shift.activities,
            });

          if (confirmed) {
            try {
              await this.handleShiftDeletion(activity);
              shouldUpdateKpis = true;
            } catch (e) {
              console.error(e);
              this.nzMessage.error(this.translate.instant('general.error'));
            }
          }
        } else {
          console.error('Shift not found');
          this.nzMessage.error(this.translate.instant('general.error'));
        }
      } else if (activity.serviceId && activity.shiftId) {
        await this.handleServiceActivityDeletion(activity);
        shouldUpdateKpis = true;
      } else if (activity.shiftId) {
        await this.handleShiftActivityDeletion(activity);
        shouldUpdateKpis = true;
      }

      if (shouldUpdateKpis && this.activityUserId) {
        this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
          this.activityUserId,
        ]);
      }
    }
  }

  private async handleShiftDeletion(activity: Activity): Promise<void> {
    await this.shiftService.deleteShift(activity.shiftId as string);
    this.closeDrawer({
      action: {
        shiftId: activity.shiftId as string,
        type: ActivityDrawerCloseActionType.DELETE_SHIFT,
      },
    });
  }

  private async handleShiftActivityDeletion(
    activity: ResolvedActivity,
  ): Promise<void> {
    try {
      await this.activitiesService.deleteActivity(activity.id as string);

      this.closeDrawer({
        action: {
          activity,
          type: ActivityDrawerCloseActionType.DELETE,
        },
      });
    } catch (e) {
      console.error(e);
      this.nzMessage.error(this.translate.instant('general.error'));
    }
  }

  private async handleServiceActivityDeletion(
    activity: ResolvedActivity,
  ): Promise<void> {
    try {
      if (activity.activityReports.length > 0) {
        await this.activitiesService.deleteActivityReports(
          activity.id as string,
        );
      }
      await this.activitiesService.updateActivity({
        id: activity.id as string,
        shiftId: null,
      });

      this.closeDrawer({
        action: {
          activity,
          type: ActivityDrawerCloseActionType.DISSOCIATE_FROM_SHIFT,
        },
      });
    } catch (e) {
      console.error(e);
      this.nzMessage.error(this.translate.instant('general.error'));
    }
  }

  confirmNavigateAway(): NzModalRef {
    return this.modalService.confirm({
      nzTitle: this.translate.instant('general.cancel'),
      nzContent: this.translate.instant(
        'page.shift_timeline.drawer.confirm.popup',
      ),
      nzOkText: this.translate.instant('general.yes'),
      nzCancelText: this.translate.instant('general.no'),
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: () => true,
    });
  }
}
